import { Injectable, signal, computed, effect } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { StorageService } from './storage.service';
import { STORAGE_KEYS } from '../constants/storage.constants';
import { DebugLoggerService } from './debug-logger.service';
import { ServerSyncService } from './server-sync.service';

export interface TestModeOverride {
    eventId: string;
    enabled: boolean;
    timestamp: number;
}

@Injectable({
    providedIn: 'root'
})
export class TestModeService {
    // NEW: Primary signal-based state
    private readonly testModeOverridesSignal = signal<Map<string, TestModeOverride>>(new Map());

    // NEW: Public signals for components
    public readonly testModeOverrides = this.testModeOverridesSignal.asReadonly();

    // NEW: Computed signals for derived state
    public readonly hasTestModeOverrides = computed(() => this.testModeOverrides().size > 0);
    public readonly testModeEventIds = computed(() =>
        Array.from(this.testModeOverrides().values())
            .filter(override => override.enabled)
            .map(override => override.eventId)
    );
    public readonly testModeEventsCount = computed(() => this.testModeEventIds().length);

    // BACKWARD COMPATIBILITY: Keep observable for gradual migration
    private testModeSubject = new BehaviorSubject<Map<string, TestModeOverride>>(new Map());
    public testModeOverrides$ = this.testModeSubject.asObservable();

    constructor(
        private storage: StorageService,
        private logger: DebugLoggerService,
        private serverSyncService: ServerSyncService
    ) {
        // Sync signal with legacy observable during transition
        effect(() => {
            this.testModeSubject.next(this.testModeOverrides());
        });

        this.loadTestModeOverrides();
    }

    /**
     * Load test mode overrides from storage
     */
    private async loadTestModeOverrides() {
        try {
            const stored = await this.storage.get(STORAGE_KEYS.TEST_MODE_OVERRIDES);
            if (stored) {
                const overrides: TestModeOverride[] = JSON.parse(stored);
                const overridesMap = new Map<string, TestModeOverride>();

                overrides.forEach(override => {
                    overridesMap.set(override.eventId, override);
                });

                this.testModeOverridesSignal.set(overridesMap);

                this.logger.log('TestMode', 'Loaded test mode overrides', {
                    count: overrides.length,
                    events: overrides.map(o => o.eventId)
                });
            }
        } catch (error) {
            this.logger.error('TestMode', 'Failed to load test mode overrides', error);
        }
    }

    /**
     * Save test mode overrides to storage
     */
    private async saveTestModeOverrides() {
        try {
            const overrides = Array.from(this.testModeOverrides().values());
            await this.storage.set(STORAGE_KEYS.TEST_MODE_OVERRIDES, JSON.stringify(overrides));

            this.logger.log('TestMode', 'Saved test mode overrides', {
                count: overrides.length,
                events: overrides.map(o => o.eventId)
            });
        } catch (error) {
            this.logger.error('TestMode', 'Failed to save test mode overrides', error);
        }
    }

    /**
     * Enable test mode for a specific event
     */
    async enableTestMode(eventId: string, eventPassword?: string): Promise<void> {
        const override: TestModeOverride = {
            eventId,
            enabled: true,
            timestamp: Date.now()
        };

        this.testModeOverridesSignal.update(current => {
            const newMap = new Map(current);
            newMap.set(eventId, override);
            return newMap;
        });

        await this.saveTestModeOverrides();

        this.logger.log('TestMode', 'Enabled test mode for event', { eventId });

        try {
            await this.serverSyncService.setEventTestMode(eventId, true, eventPassword);
        } catch (error) {
            this.logger.error('TestMode', 'Failed to sync test mode to server', error);
        }
    }

    /**
     * Disable test mode for a specific event
     */
    async disableTestMode(eventId: string, eventPassword?: string): Promise<void> {
        const currentOverrides = this.testModeOverrides();
        if (currentOverrides.has(eventId)) {
            this.testModeOverridesSignal.update(current => {
                const newMap = new Map(current);
                newMap.delete(eventId);
                return newMap;
            });

            await this.saveTestModeOverrides();

            this.logger.log('TestMode', 'Disabled test mode for event', { eventId });

            try {
                await this.serverSyncService.setEventTestMode(eventId, false, eventPassword);
            } catch (error) {
                this.logger.error('TestMode', 'Failed to sync test mode to server', error);
            }
        }
    }

    /**
     * Check if test mode is enabled for a specific event
     */
    isTestModeEnabled(eventId: string): boolean {
        const override = this.testModeOverrides().get(eventId);
        return override?.enabled === true;
    }

    /**
     * Get all events with test mode enabled
     */
    getTestModeEvents(): string[] {
        return this.testModeEventIds();
    }

    /**
     * Clear all test mode overrides
     */
    async clearAllTestModes(): Promise<void> {
        this.testModeOverridesSignal.set(new Map());
        await this.saveTestModeOverrides();

        this.logger.log('TestMode', 'Cleared all test mode overrides');
    }

    /**
     * Clear test mode for events older than specified days
     */
    async clearOldTestModes(daysOld: number = 30): Promise<void> {
        const cutoffTime = Date.now() - (daysOld * 24 * 60 * 60 * 1000);
        const currentOverrides = this.testModeOverrides();
        const initialCount = currentOverrides.size;

        this.testModeOverridesSignal.update(current => {
            const newMap = new Map<string, TestModeOverride>();

            for (const [eventId, override] of current.entries()) {
                if (override.timestamp >= cutoffTime) {
                    newMap.set(eventId, override);
                }
            }

            return newMap;
        });

        if (this.testModeOverrides().size < initialCount) {
            await this.saveTestModeOverrides();

            this.logger.log('TestMode', 'Cleared old test mode overrides', {
                removed: initialCount - this.testModeOverrides().size,
                remaining: this.testModeOverrides().size
            });
        }
    }
} 